import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiChevronDown,
  FiChevronUp,
  FiTrash2,
  FiExternalLink,
  FiSearch,
  FiCalendar,
  FiCopy,
  FiShare,
  FiCheck,
} from "react-icons/fi";
import {
  getSavedPosts,
  deleteSavedPost,
} from "../NormalPostGenerator/utils/firebaseUtils";
import { getDatabase, ref, get } from "firebase/database";
import {
  initFacebookSDK,
  uploadImage,
} from "../NormalPostGenerator/utils/facebookUtils";
import axios from "axios";

const SavedPostsPage = () => {
  const navigate = useNavigate();
  const [savedPosts, setSavedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // desc = mới nhất, asc = cũ nhất

  // State cho việc sao chép nội dung
  const [copiedPostId, setCopiedPostId] = useState(null);

  // States cho dropdown fanpage và đăng bài
  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState({});
  const [hasFanpages, setHasFanpages] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState({});
  const [isPublishing, setIsPublishing] = useState({});

  // Refs
  const dropdownRefs = useRef({});

  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    fetchSavedPosts();
    fetchFanpages();
  }, [navigate]);

  // Lấy danh sách fanpage từ Firebase
  const fetchFanpages = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const connectedFanpagesRef = ref(db, `users/${userId}/connectedFanpages`);
      const connectedFanpagesSnapshot = await get(connectedFanpagesRef);

      if (connectedFanpagesSnapshot.exists()) {
        const connectedData = connectedFanpagesSnapshot.val();
        const fanpageList = Object.values(connectedData);

        console.log("Fanpages loaded from Firebase:", fanpageList);

        if (fanpageList.length > 0) {
          setFanpages(fanpageList);
          setHasFanpages(true);
        } else {
          setHasFanpages(false);
          setFanpages([]);
        }
      } else {
        console.log("Không tìm thấy fanpage nào trong Firebase");
        setHasFanpages(false);
        setFanpages([]);
      }
    } catch (error) {
      console.error("Error fetching fanpages from Firebase:", error);
      setHasFanpages(false);
      setFanpages([]);
    }
  };

  // Lấy danh sách bài viết đã lưu
  const fetchSavedPosts = async () => {
    setIsLoading(true);
    try {
      const result = await getSavedPosts();
      if (result.success && result.posts) {
        // Chuyển từ object sang mảng để dễ sắp xếp
        const postsArray = Object.keys(result.posts).map((key) => ({
          id: key,
          ...result.posts[key],
        }));
        setSavedPosts(postsArray);

        // Khởi tạo state cho dropdown và publishing
        const initialDropdownState = {};
        const initialPublishingState = {};
        const initialSelectedFanpages = {};

        postsArray.forEach((post) => {
          initialDropdownState[post.id] = false;
          initialPublishingState[post.id] = false;
          initialSelectedFanpages[post.id] = [];
        });

        setIsDropdownOpen(initialDropdownState);
        setIsPublishing(initialPublishingState);
        setSelectedFanpages(initialSelectedFanpages);

        // Chọn bài viết đầu tiên mặc định
        if (postsArray.length > 0) {
          setSelectedPostId(postsArray[0].id);
        }
      } else {
        setSavedPosts([]);
      }
    } catch (error) {
      console.error("Lỗi khi lấy danh sách bài viết đã lưu:", error);
      toast.error("Không thể lấy danh sách bài viết đã lưu", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Xử lý xóa bài viết
  const handleDeletePost = async (postId) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa bài viết này không?")) {
      try {
        const result = await deleteSavedPost(postId);
        if (result.success) {
          const updatedPosts = savedPosts.filter((post) => post.id !== postId);
          setSavedPosts(updatedPosts);
          toast.success("Xóa bài viết thành công", {
            position: "top-right",
            autoClose: 3000,
          });

          // Nếu xóa bài viết đang được chọn, chọn bài đầu tiên trong danh sách mới
          if (selectedPostId === postId && updatedPosts.length > 0) {
            setSelectedPostId(updatedPosts[0].id);
          } else if (updatedPosts.length === 0) {
            setSelectedPostId(null);
          }
        } else {
          toast.error(`Lỗi khi xóa bài viết: ${result.error}`, {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error("Lỗi khi xóa bài viết:", error);
        toast.error(`Lỗi khi xóa bài viết: ${error.message}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };

  // Xử lý sao chép nội dung
  const handleCopyContent = (postId, content) => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopiedPostId(postId);
        toast.success("Đã sao chép nội dung bài viết", {
          position: "top-right",
          autoClose: 2000,
        });

        // Tự động xóa trạng thái sao chép sau 2 giây
        setTimeout(() => {
          setCopiedPostId(null);
        }, 2000);
      })
      .catch((err) => {
        console.error("Lỗi khi sao chép: ", err);
        toast.error("Không thể sao chép nội dung bài viết", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  // Toggle dropdown fanpage
  const toggleDropdown = (postId) => {
    setIsDropdownOpen((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  // Xử lý chọn fanpage
  const handleFanpageSelect = (postId, fanpage) => {
    setSelectedFanpages((prev) => ({
      ...prev,
      [postId]: [fanpage], // Chỉ chọn một fanpage
    }));

    // Đóng dropdown
    setIsDropdownOpen((prev) => ({
      ...prev,
      [postId]: false,
    }));
  };

  // Xử lý đăng bài lên fanpage
  const handlePublishToFanpage = async (postId, content, images) => {
    // Kiểm tra xem đã chọn fanpage chưa
    if (!selectedFanpages[postId] || selectedFanpages[postId].length === 0) {
      toast.warning("Vui lòng chọn Fanpage trước khi đăng bài", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Cập nhật trạng thái publishing
    setIsPublishing((prev) => ({
      ...prev,
      [postId]: true,
    }));

    try {
      // Khởi tạo Facebook SDK
      await initFacebookSDK();

      const fanpage = selectedFanpages[postId][0];

      // Tải lên ảnh trước nếu có
      let attachedMedia = [];
      if (images && images.length > 0) {
        try {
          // Sử dụng hàm uploadImage từ facebookUtils để tải ảnh lên
          for (const imageUrl of images) {
            try {
              const photoId = await uploadImage(imageUrl, fanpage);
              attachedMedia.push({ media_fbid: photoId });
            } catch (imgError) {
              console.error("Lỗi khi tải ảnh lên Facebook:", imgError);
              // Tiếp tục với các ảnh khác nếu có lỗi
            }
          }
        } catch (imgError) {
          console.error("Lỗi khi tải ảnh lên Facebook:", imgError);
        }
      }

      // Chuẩn bị dữ liệu bài đăng
      const postData = {
        message: content,
        access_token: fanpage.access_token,
      };

      // Thêm ảnh đã tải lên vào bài viết nếu có
      if (attachedMedia.length > 0) {
        postData.attached_media = attachedMedia;
      }

      // Gọi API đăng bài
      await new Promise((resolve, reject) => {
        window.FB.api(
          `/${fanpage.id}/feed`,
          "POST",
          postData,
          function (response) {
            if (response && !response.error) {
              toast.success(`Đã đăng bài lên fanpage ${fanpage.name}`, {
                position: "top-right",
                autoClose: 3000,
              });
              resolve(response);
            } else {
              console.error("Lỗi khi đăng bài lên Facebook:", response?.error);
              toast.error(
                `Lỗi khi đăng bài: ${
                  response?.error?.message || "Lỗi không xác định"
                }`,
                {
                  position: "top-right",
                  autoClose: 5000,
                }
              );
              reject(response?.error);
            }

            // Cập nhật trạng thái publishing
            setIsPublishing((prev) => ({
              ...prev,
              [postId]: false,
            }));
          }
        );
      });
    } catch (error) {
      console.error("Lỗi khi đăng bài:", error);
      toast.error(`Lỗi khi đăng bài: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
      });

      setIsPublishing((prev) => ({
        ...prev,
        [postId]: false,
      }));
    }
  };

  // Định dạng thời gian
  const formatDate = (timestamp) => {
    if (!timestamp) return "Không xác định";
    const date = new Date(parseInt(timestamp));
    return date.toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Kiểm tra xem bài viết có phải là đăng lên mạng xã hội không
  const isSocialMediaPost = (post) => {
    return (
      post.metadata &&
      post.metadata.publishingPlatform &&
      ((typeof post.metadata.publishingPlatform === "object" &&
        post.metadata.publishingPlatform.id === 1) ||
        post.metadata.publishingPlatform === 1)
    );
  };

  // Lọc bài viết theo tìm kiếm
  const filteredPosts = savedPosts.filter((post) => {
    const title = post.title || "";
    const content = post.content || "";
    const lowercaseQuery = searchQuery.toLowerCase();
    return (
      title.toLowerCase().includes(lowercaseQuery) ||
      content.toLowerCase().includes(lowercaseQuery)
    );
  });

  // Sắp xếp bài viết
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    const timeA = parseInt(a.savedAt || a.createdAt || 0);
    const timeB = parseInt(b.savedAt || b.createdAt || 0);
    return sortOrder === "desc" ? timeB - timeA : timeA - timeB;
  });

  // Xử lý chọn bài viết
  const handleSelectPost = (postId) => {
    setSelectedPostId(postId);
  };

  // Lấy bài viết được chọn
  const selectedPost = selectedPostId
    ? savedPosts.find((post) => post.id === selectedPostId)
    : sortedPosts.length > 0
    ? sortedPosts[0]
    : null;

  // Effect để xử lý click bên ngoài dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach((postId) => {
        if (
          dropdownRefs.current[postId] &&
          !dropdownRefs.current[postId].contains(event.target)
        ) {
          setIsDropdownOpen((prev) => ({
            ...prev,
            [postId]: false,
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // UI cho chi tiết bài viết
  const renderPostDetail = (post) => {
    if (!post) return null;

    return (
      <div className="bg-white rounded-md shadow-sm p-4 h-full overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {post.title || "Bài viết không có tiêu đề"}
          </h2>
          <div className="flex items-center">
            <button
              onClick={() => handleCopyContent(post.id, post.content)}
              className="mr-2 p-2 text-blue-600 hover:bg-blue-50 rounded-full"
              title="Sao chép nội dung"
            >
              {copiedPostId === post.id ? <FiCheck /> : <FiCopy />}
            </button>
            <button
              onClick={() => handleDeletePost(post.id)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-full"
              title="Xóa bài viết"
            >
              <FiTrash2 />
            </button>
          </div>
        </div>

        <div className="flex items-center text-sm text-gray-500 mb-4">
          <FiCalendar className="mr-1" />
          <span>{formatDate(post.savedAt || post.createdAt)}</span>
        </div>

        {/* Nội dung bài viết */}
        <div className="prose max-w-none mb-6 whitespace-pre-wrap">
          {post.content}
        </div>

        {/* Hiển thị ảnh nếu có */}
        {post.images && post.images.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Hình ảnh:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {post.images.map((image, index) => (
                <div key={index} className="relative aspect-w-16 aspect-h-9">
                  <img
                    src={image}
                    alt={`Hình ${index + 1}`}
                    className="object-cover rounded-md w-full h-full"
                  />
                  <a
                    href={image}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 right-2 p-1 bg-black bg-opacity-50 rounded-full text-white"
                    title="Mở trong tab mới"
                  >
                    <FiExternalLink />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phần chọn fanpage và đăng bài nếu bài viết là đăng lên mạng xã hội */}
        {isSocialMediaPost(post) && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              {hasFanpages ? (
                <>
                  <div
                    className="relative w-full sm:w-1/2"
                    ref={(el) => (dropdownRefs.current[post.id] = el)}
                  >
                    <button
                      onClick={() => toggleDropdown(post.id)}
                      className="w-full px-4 py-2 text-left border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center"
                    >
                      <span>
                        {selectedFanpages[post.id] &&
                        selectedFanpages[post.id].length > 0
                          ? selectedFanpages[post.id][0].name
                          : "Chọn Fanpage"}
                      </span>
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {isDropdownOpen[post.id] && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
                        {fanpages.map((fanpage) => (
                          <div
                            key={fanpage.id}
                            onClick={() =>
                              handleFanpageSelect(post.id, fanpage)
                            }
                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between ${
                              selectedFanpages[post.id]?.find(
                                (fp) => fp.id === fanpage.id
                              )
                                ? "bg-blue-50"
                                : ""
                            }`}
                          >
                            <span className="text-left truncate">
                              {fanpage.name}
                            </span>
                            {selectedFanpages[post.id]?.find(
                              (fp) => fp.id === fanpage.id
                            ) && <span className="text-blue-600">✓</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      handlePublishToFanpage(post.id, post.content, post.images)
                    }
                    disabled={
                      !selectedFanpages[post.id] ||
                      selectedFanpages[post.id].length === 0 ||
                      isPublishing[post.id]
                    }
                    className={`px-4 py-2 rounded-md text-white flex items-center whitespace-nowrap
                      ${
                        !selectedFanpages[post.id] ||
                        selectedFanpages[post.id].length === 0 ||
                        isPublishing[post.id]
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                  >
                    {isPublishing[post.id] ? (
                      <>
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-r-transparent rounded-full"></div>
                        <span>Đang đăng...</span>
                      </>
                    ) : (
                      <>
                        <FiShare className="mr-2" />
                        <span>Đăng lên Fanpage</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <div className="w-full text-center p-3 bg-yellow-50 text-yellow-700 rounded-md">
                  <p>
                    Bạn chưa kết nối Fanpage nào. Vui lòng vào trang Hồ sơ để
                    kết nối.
                  </p>
                  <button
                    onClick={() => navigate("/profile")}
                    className="mt-2 px-4 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Đi đến trang Hồ sơ
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-screen-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
          Bài viết đã lưu
        </h1>

        {/* Công cụ tìm kiếm và sắp xếp */}
        <div className="bg-white p-4 rounded-md shadow-sm mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Tìm kiếm bài viết..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div>
              <select
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Mới nhất trước</option>
                <option value="asc">Cũ nhất trước</option>
              </select>
            </div>
          </div>
        </div>

        {/* Layout chính: danh sách bên trái, chi tiết bên phải */}
        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Đang tải danh sách bài viết...</p>
          </div>
        ) : sortedPosts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-md shadow-sm">
            <div className="text-gray-400 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <p className="text-xl font-medium text-gray-600 mb-2">
              Chưa có bài viết nào được lưu
            </p>
            <p className="text-gray-500 mb-6">
              Hãy tạo và lưu bài viết để xem chúng ở đây
            </p>
            <button
              onClick={() => navigate("/normal-post-generator")}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Tạo bài viết mới
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Danh sách bài viết (bên trái) */}
            <div className="md:col-span-1 bg-white rounded-md shadow-sm overflow-hidden h-full overflow-y-auto">
              <div className="divide-y divide-gray-100">
                {sortedPosts.map((post) => (
                  <div
                    key={post.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedPostId === post.id
                        ? "bg-blue-50 border-l-4 border-blue-500"
                        : ""
                    }`}
                    onClick={() => handleSelectPost(post.id)}
                  >
                    <h3 className="font-medium text-gray-800 mb-1 line-clamp-1">
                      {post.title || "Bài viết không có tiêu đề"}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-2 mb-2">
                      {post.content}
                    </p>
                    <div className="flex items-center text-xs text-gray-400">
                      <FiCalendar className="mr-1" />
                      <span>{formatDate(post.savedAt || post.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chi tiết bài viết (bên phải) */}
            <div className="md:col-span-2 h-full overflow-y-auto">
              {renderPostDetail(selectedPost)}
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        pauseOnHover={true}
        draggable={true}
      />
    </div>
  );
};

export default SavedPostsPage;
